import { Badge, Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import { copyToClipboard } from 'utils/helpers/copyToClipboard';
import TableEmptyRow from 'components/others/TableEmptyRow';
import type { ApiKeyType, ApiKeyTypeRow } from '../types';
import SolarIcon from 'components/icons/SolarIcon';

interface ApiKeysTableProps {
    type: ApiKeyType;
    data: ApiKeyTypeRow[];
    onEdit: (row: ApiKeyTypeRow) => void;
    onDelete: (row: ApiKeyTypeRow) => void;
}

const maskApiKey = (key: string): string => {
    if (!key) return '';
    if (key.length <= 10) return key;
    return `${key.slice(0, 5)}...${key.slice(-5)}`;
};

const ApiKeysTable = ({ type, data, onEdit, onDelete }: ApiKeysTableProps) => {
    const { t } = useTranslation(['Settings']);
    const { showToast } = useToast();
    const actionBtnClass = 'p-0 d-inline-flex align-items-center justify-content-center';
    const minTableWidth = type === 'out' ? 1080 : 1120;

    return (
        <Table hover responsive className="mb-0" style={{ tableLayout: 'fixed', minWidth: `${minTableWidth}px` }}>
            <thead>
                <tr className="bg-light">
                    <td style={{ width: '180px' }}>{t('apiKeys.table.colName')}</td>
                    {type === 'out' && <td style={{ width: '280px' }}>{t('apiKeys.table.colUrl')}</td>}
                    {type === 'in' && <td style={{ width: '260px' }}>{t('apiKeys.table.colDomains')}</td>}
                    <td style={{ width: '220px' }}>{t('apiKeys.table.colApiKey')}</td>
                    <td style={{ minWidth: type === 'out' ? '280px' : '320px' }}>
                        {t(type === 'out' ? 'apiKeys.table.colEvents' : 'apiKeys.table.colAccesses')}
                    </td>
                    <td style={{ width: '120px' }}>{t('apiKeys.table.colStatus')}</td>
                    <td style={{ width: '90px' }} className="text-end">
                        {t('apiKeys.table.colActions')}
                    </td>
                </tr>
            </thead>
            <tbody>
                {data.length === 0 ? (
                    <TableEmptyRow colSpan={6} className="text-muted py-3">
                        {t('apiKeys.table.empty')}
                    </TableEmptyRow>
                ) : (
                    data.map((row) => (
                        <tr key={row.ID}>
                            <td>{row.keyName}</td>
                            {type === 'out' && (
                                <td>
                                    <span className="text-break">{row.webhookUrl || '-'}</span>
                                </td>
                            )}
                            {type === 'in' && (
                                <td>
                                    <span className="text-break">{row.allowedDomains || '-'}</span>
                                </td>
                            )}
                            <td>
                                <div className="d-flex align-items-center gap-2">
                                    <code>{maskApiKey(row.apiKey || '')}</code>
                                    <Button
                                        size="sm"
                                        variant="link"
                                        className="p-0 text-secondary"
                                        onClick={async () => {
                                            const copied = await copyToClipboard(row.apiKey || '');
                                            showToast(
                                                copied ? t('apiKeys.toasts.copied') : t('apiKeys.errors.copyFailed'),
                                                copied ? 'success' : 'danger',
                                            );
                                        }}
                                    >
                                        <SolarIcon name="copy" className="me-1" />
                                    </Button>
                                </div>
                            </td>
                            <td>
                                {(type === 'in'
                                    ? (Array.isArray(row.accessJson) ? row.accessJson : [])
                                    : (Array.isArray(row.eventTypesJson) ? row.eventTypesJson : [])
                                ).map((item) => (
                                    <Badge bg="light" text="dark" className="me-1 mb-1" key={item}>
                                        {t(
                                            type === 'in' ? `apiKeys.access.${item}` : `apiKeys.events.${item}`,
                                            { defaultValue: item },
                                        )}
                                    </Badge>
                                ))}
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                                <Badge bg={row.isActive ? 'success' : 'secondary'}>
                                    {t(row.isActive ? 'apiKeys.table.active' : 'apiKeys.table.disabled')}
                                </Badge>
                            </td>
                            <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                                <Button
                                    size="sm"
                                    variant="link"
                                    className={`text-info me-2 ${actionBtnClass}`}
                                    onClick={() => onEdit(row)}
                                    title={t('common:buttons.change')}
                                >
                                    <SolarIcon name="pencil-square" />
                                </Button>
                                <Button
                                    size="sm"
                                    variant="link"
                                    className={`text-danger ${actionBtnClass}`}
                                    onClick={() => onDelete(row)}
                                    title={t('common:buttons.delete')}
                                >
                                    <SolarIcon name="trash" />
                                </Button>
                            </td>
                        </tr>
                    ))
                )}
            </tbody>
        </Table>
    );
};

export default ApiKeysTable;
